import type { ComponentIR } from "../ir.js";
import type { GeneratedFile, GenerateOptions, Generator } from "./types.js";
import {
  interfaceFromSchema,
  operationTypeNames,
  schemaRefToTsType,
} from "./schema-to-ts.js";

function generateOperationTypes(
  operationName: string,
  op: ComponentIR["operations"][string],
): string {
  const names = operationTypeNames(operationName);
  const parts: string[] = [];

  if (op.input) {
    const iface = interfaceFromSchema(
      names.input,
      op.input,
      "Record<string, unknown>",
    );
    parts.push(iface ?? `export type ${names.input} = Record<string, unknown>;\n`);
  } else {
    parts.push(`export type ${names.input} = Record<string, unknown>;\n`);
  }

  const outputIface = interfaceFromSchema(
    names.output,
    op.output.success,
    "unknown",
  );
  parts.push(outputIface ?? `export type ${names.output} = unknown;\n`);

  if (op.options.length > 0) {
    const fields = op.options.map((opt) => {
      const fieldName = opt.name.includes("-") || opt.name.includes("_")
        ? JSON.stringify(opt.name)
        : opt.name;
      const optional = opt.required ? "" : "?";
      const tsType = schemaRefToTsType({ schema: opt.schema });
      const desc = opt.description
        ? `  /** ${opt.description} */\n`
        : "";
      return `${desc}  ${fieldName}${optional}: ${tsType};`;
    });
    parts.push(`export interface ${names.options} {\n${fields.join("\n")}\n}\n`);
  } else {
    parts.push(`export type ${names.options} = Record<string, never>;\n`);
  }

  if (op.output.failed) {
    const failureIface = interfaceFromSchema(
      names.failure,
      op.output.failed,
      "unknown",
    );
    parts.push(failureIface ?? `export type ${names.failure} = unknown;\n`);
  }

  if (op.errorModel.failureClasses.length > 0) {
    const union = op.errorModel.failureClasses
      .map((c) => JSON.stringify(c))
      .join(" | ");
    parts.push(`export type ${names.failureClass} = ${union};\n`);
  }

  return parts.join("\n");
}

export const typesGenerator: Generator = {
  generate(ir: ComponentIR, _options: GenerateOptions): GeneratedFile[] {
    const operationBlocks = Object.entries(ir.operations).map(
      ([name, op]) => generateOperationTypes(name, op),
    );

    const content = `/** Auto-generated by @aaac/contracts. Do not edit. */
/* eslint-disable */

${operationBlocks.join("\n")}
`;

    return [
      {
        path: "component.types.ts",
        content,
        overwrite: true,
      },
    ];
  },
};
